/**
 * `<caos-log-viewer>` — a log read as timestamp, event and detail, not scrolled as a wall of text.
 * (Catalog: Developer surfaces › LogViewer — one log read as structured lines, filtered by category or by text.)
 *
 * READING A LOG IS A STRUCTURED READ. Timestamp, event and detail as three columns, with a filter
 * down to the lines that matter, is the difference between a log you can use and a log you scroll.
 * That is the single most useful thing the reference tool does that the incumbent console does not,
 * and it is why this is a component rather than a `<pre>`.
 *
 * THE FILTER IS BY CATEGORY, NOT BY TEXT SEARCH FOR THE CATEGORY NAME. "Show me only the debug
 * lines" typed into a search box also matches every line whose detail happens to contain the word,
 * and misses debug lines that do not say so. The two narrow independently and intersect, because
 * "the debug lines mentioning this record id" is the actual question.
 *
 * THE LINE NUMBER IS THE ORIGINAL ONE. A filtered log renumbered 1..40 destroys the only thing that
 * connects a line back to the raw text somebody may also be looking at, or to a colleague's
 * screenshot. Filtering hides lines; it does not renumber them.
 *
 * A LOG THAT COULD NOT BE PARSED IS NOT AN EMPTY LOG. Handing this component raw text it cannot make
 * lines out of gets the raw text, shown as raw text, and a line saying so — not a blank panel that
 * reads as a log with nothing in it.
 *
 * Styling seams: `::part(viewer)`, `::part(toolbar)`, `::part(filter)`, `::part(search)`,
 * `::part(summary)`, `::part(lines)`, `::part(line)`, `::part(at)`, `::part(event)`,
 * `::part(detail)`, `::part(raw)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** What kind of line this is. The categories the filter offers. */
export type CaosLogCategory = 'executable' | 'debug' | 'system' | 'error';

/** How each category reads to a person. */
export const LOG_CATEGORY_LABELS: Readonly<Record<CaosLogCategory, string>> = {
  executable: 'Executable',
  debug: 'Debug',
  system: 'System',
  error: 'Errors',
};

/** One line of a log. */
export interface CaosLogLine {
  /** Its position in the ORIGINAL log, 1-based. Never renumbered by filtering. */
  readonly number: number;
  /** When, as the log recorded it — a timestamp string, not necessarily ISO. */
  readonly at?: string;
  /** What happened — the event name the platform wrote. */
  readonly event?: string;
  /** The rest of the line. */
  readonly detail?: string;
  readonly category?: CaosLogCategory;
}

const sheet = makeSheet(`
  :host { display: block; }

  .viewer { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .toolbar { display: flex; gap: var(--caos-space-2); align-items: center; flex-wrap: wrap; }
  .toolbar select, .toolbar input {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 9px;
  }
  .toolbar input { flex: 1 1 200px; min-width: 150px; }
  .toolbar select:focus-visible, .toolbar input:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .summary { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }

  .lines {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    max-height: 420px;
    overflow: auto;
    background: var(--caos-color-surface);
  }

  .line {
    display: grid;
    grid-template-columns: 48px 132px 148px minmax(0, 1fr);
    gap: var(--caos-space-2);
    padding: 2px 8px;
    font-family: var(--caos-font-mono);
    font-size: 11.5px;
    line-height: 1.6;
    border-bottom: 1px solid transparent;
  }
  .line:nth-child(odd) { background: var(--caos-color-surface-2); }
  .line[data-category="error"] { color: var(--caos-color-danger-text); }
  .line[data-category="debug"] { color: var(--caos-color-info-text); }

  .num { color: var(--caos-color-text-ghost); text-align: right; }
  .at { color: var(--caos-color-text-muted); }
  .event { color: var(--caos-color-text); font-weight: 600; }
  .detail { color: var(--caos-color-text); white-space: pre-wrap; word-break: break-word; }

  .raw {
    margin: 0;
    padding: var(--caos-space-3);
    font-family: var(--caos-font-mono);
    font-size: 11.5px;
    line-height: 1.55;
    color: var(--caos-color-text);
    white-space: pre-wrap;
    word-break: break-word;
  }

  .note { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); font-style: italic; }
`);

/** Narrow lines by category and by text; the two intersect. */
export function filterLogLines(
  lines: readonly CaosLogLine[],
  category: string | null,
  term: string,
): readonly CaosLogLine[] {
  const q = term.trim().toLowerCase();
  return lines.filter((l) => {
    if (category !== null && category !== '' && l.category !== category) return false;
    if (q === '') return true;
    return [l.event, l.detail, l.at]
      .filter((v): v is string => typeof v === 'string')
      .some((v) => v.toLowerCase().includes(q));
  });
}

export class CaosLogViewer extends CaosElement {
  static get observedAttributes(): string[] {
    return ['category', 'search', 'empty-text'];
  }

  private filterEl: HTMLSelectElement;
  private searchEl: HTMLInputElement;
  private summaryEl: HTMLParagraphElement;
  private linesEl: HTMLDivElement;

  private data: readonly CaosLogLine[] = [];
  private rawText: string | null = null;

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'viewer';
    wrap.setAttribute('part', 'viewer');

    const toolbar = document.createElement('div');
    toolbar.className = 'toolbar';
    toolbar.setAttribute('part', 'toolbar');

    this.filterEl = document.createElement('select');
    this.filterEl.setAttribute('part', 'filter');
    this.filterEl.setAttribute('aria-label', 'Line category');
    this.filterEl.addEventListener('change', () => {
      if (this.filterEl.value === '') this.removeAttribute('category');
      else this.setAttribute('category', this.filterEl.value);
      this.dispatchEvent(
        new CustomEvent('caos-log-filter', {
          bubbles: true,
          composed: true,
          detail: { category: this.getAttribute('category'), search: this.getAttribute('search') ?? '' },
        }),
      );
    });

    this.searchEl = document.createElement('input');
    this.searchEl.type = 'search';
    this.searchEl.setAttribute('part', 'search');
    this.searchEl.placeholder = 'Search this log';
    this.searchEl.setAttribute('aria-label', 'Search the log lines');
    this.searchEl.addEventListener('input', () => this.setAttribute('search', this.searchEl.value));

    toolbar.append(this.filterEl, this.searchEl);

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.linesEl = document.createElement('div');
    this.linesEl.className = 'lines';
    this.linesEl.setAttribute('part', 'lines');

    wrap.append(toolbar, this.summaryEl, this.linesEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, _before: string | null, after: string | null): void {
    if (name === 'search' && this.searchEl.value !== (after ?? '')) this.searchEl.value = after ?? '';
    this.render();
  }

  get lines(): readonly CaosLogLine[] {
    return this.data;
  }

  set lines(next: readonly CaosLogLine[]) {
    this.data = Array.isArray(next) ? next : [];
    // Structured lines win: a caller that has both has parsed the text, and the raw copy is then
    // only a fallback for a log nobody could make lines out of.
    this.render();
  }

  /**
   * The log as raw text, for a log that could not be parsed into lines.
   *
   * Shown ONLY when there are no lines. Handing this component text it cannot structure gets the
   * text, said to be unstructured — never a blank panel that reads as a log with nothing in it.
   */
  get raw(): string | null {
    return this.rawText;
  }

  set raw(text: string | null) {
    this.rawText = text;
    this.render();
  }

  /** The lines surviving both filters. */
  get visibleLines(): readonly CaosLogLine[] {
    return filterLogLines(this.data, this.getAttribute('category'), this.getAttribute('search') ?? '');
  }

  /** The categories present in this log, in the order the vocabulary lists them. */
  get categories(): readonly CaosLogCategory[] {
    const present = new Set(this.data.map((l) => l.category).filter((c): c is CaosLogCategory => c !== undefined));
    return (Object.keys(LOG_CATEGORY_LABELS) as CaosLogCategory[]).filter((c) => present.has(c));
  }

  private render(): void {
    this.renderFilter();

    if (this.data.length === 0 && this.rawText !== null) {
      const note = document.createElement('p');
      note.className = 'note';
      note.textContent = 'This log could not be read as lines. It is shown as it arrived.';
      const pre = document.createElement('pre');
      pre.className = 'raw';
      pre.setAttribute('part', 'raw');
      pre.textContent = this.rawText;
      this.linesEl.replaceChildren(pre);
      this.summaryEl.replaceChildren(note);
      return;
    }

    const visible = this.visibleLines;
    const nodes: Node[] = [];
    const strong = document.createElement('strong');
    strong.textContent =
      visible.length === this.data.length
        ? `${this.data.length} ${this.data.length === 1 ? 'line' : 'lines'}`
        : `${visible.length} of ${this.data.length} lines`;
    nodes.push(strong);
    const errors = this.data.filter((l) => l.category === 'error').length;
    if (errors > 0) nodes.push(document.createTextNode(` · ${errors} ${errors === 1 ? 'error' : 'errors'}`));
    this.summaryEl.replaceChildren(...nodes);

    if (visible.length === 0) {
      const note = document.createElement('p');
      note.className = 'note';
      note.textContent =
        this.data.length === 0
          ? (this.getAttribute('empty-text') ?? 'This log is empty.')
          : 'No lines match the filters.';
      this.linesEl.replaceChildren(note);
      return;
    }

    this.linesEl.replaceChildren(...visible.map((l) => this.lineFor(l)));
  }

  private renderFilter(): void {
    const chosen = this.getAttribute('category') ?? '';
    this.filterEl.replaceChildren(
      ...[
        { v: '', t: 'All lines' },
        ...this.categories.map((c) => ({ v: c, t: LOG_CATEGORY_LABELS[c] })),
      ].map(({ v, t }) => {
        const o = document.createElement('option');
        o.value = v;
        o.textContent = t;
        o.selected = v === chosen;
        return o;
      }),
    );
  }

  private lineFor(line: CaosLogLine): HTMLElement {
    const row = document.createElement('div');
    row.className = 'line';
    row.setAttribute('part', 'line');
    if (line.category !== undefined) row.setAttribute('data-category', line.category);

    const num = document.createElement('span');
    num.className = 'num';
    // The ORIGINAL number. A filtered log renumbered 1..40 destroys the only thing connecting a line
    // back to the raw text, or to somebody else's screenshot of it.
    num.textContent = String(line.number);

    const at = document.createElement('span');
    at.className = 'at';
    at.setAttribute('part', 'at');
    at.textContent = line.at ?? '';

    const event = document.createElement('span');
    event.className = 'event';
    event.setAttribute('part', 'event');
    event.textContent = line.event ?? '';

    const detail = document.createElement('span');
    detail.className = 'detail';
    detail.setAttribute('part', 'detail');
    detail.textContent = line.detail ?? '';

    row.append(num, at, event, detail);
    return row;
  }
}

define('caos-log-viewer', CaosLogViewer);

declare global {
  interface HTMLElementTagNameMap {
    'caos-log-viewer': CaosLogViewer;
  }
}
